import { useInfiniteQuery } from '@zg-rentals/react-query5';

import { RentalManagerAccountViewZrmV2WebApiHandler_list } from '@zg-rentals/ts-rental-manager-api-beta-client';

import { transformFiltersToApiParams } from './zram';

import type { FilterState } from '../../containers/ZRAMPropertiesPage';

import {
  type AccountViewListingGroupDto,
  type AccountViewListingGroupsResponseDto,
  type ListingCountDto,
  type RentalManagerKeyPagerDto,
} from '@zg-rentals/ts-rental-manager-api-client';

/** Flattened shape returned when pages are loaded; empty object when no data yet */
export type GroupablePropertiesData = {
  listingGroups?: Array<AccountViewListingGroupDto>;
  listingCount?: ListingCountDto;
  pager?: RentalManagerKeyPagerDto;
};

export type UseGetGroupablePropertiesDataOptions = {
  enabled?: boolean;
};

export const useGetGroupablePropertiesData = (
  filters: FilterState,
  options: UseGetGroupablePropertiesDataOptions = {},
) => {
  const { enabled = true } = options;
  const searchText = filters.searchText ?? '';
  const subNavFilterType = filters.subNavFilterType ?? 'all';

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isPending, isError, isFetching } = useInfiniteQuery<
    AccountViewListingGroupsResponseDto,
    Error,
    { pages: Array<AccountViewListingGroupsResponseDto> },
    Array<string>,
    string
  >({
    queryKey: ['getGroupableProperties', searchText, subNavFilterType],
    queryFn: async ({ pageParam }) => {
      return RentalManagerAccountViewZrmV2WebApiHandler_list({
        ...transformFiltersToApiParams({ searchText, subNavFilterType }),
        startKeyExclusive: pageParam,
      });
    },
    initialPageParam: '',
    getNextPageParam: (lastPage) => (lastPage.pager?.hasMore ? lastPage.pager.endKey : undefined),
    enabled,
    // Keep previous data during refetch to prevent UI flicker
    placeholderData: (previousData) => {
      if (!previousData?.pages?.length) {
        return;
      }

      return previousData;
    },
  });

  const propertiesData: GroupablePropertiesData = data?.pages.length
    ? {
        listingGroups: data.pages.flatMap((page) => page.listingGroups),
        listingCount: data.pages[0].listingCount,
        pager: data.pages[data.pages.length - 1].pager,
      }
    : {};

  return {
    data: propertiesData,
    isPending,
    isFetching,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
};
